/**
 * ROLE_GROUPS — single source of truth for route access control (rewamp).
 *
 * Each group is a named bundle of role codes passed to <ProtectedRoute access_roles={...} />.
 * Define access ONCE here and reference it by name in RewampRoutes.js — never inline a
 * role array in the routes file. This keeps RBAC auditable and lets you change who can
 * reach a set of routes by editing a single list.
 *
 * Role codes come from the user-info payload (localStorage "DfsWeb.user-info" → roles[].code),
 * read by getUserRoles() in src/components/Utils.js.
 *
 * 📖 Docs + route map: .agent/docs/rewamp-routes.md  (skill: /rewamp-routes)
 * ⚠️  Changing a group changes EVERY route that uses it. To gate a single route differently,
 *     add a new named group below rather than editing an existing one.
 */
import { pgrRoles } from "../../components/Utils";

/** Canonical role codes — reference these instead of typing strings, to avoid typos. */
export const ROLES = Object.freeze({
  AGENT: "ASSISTEDMODE_AGENT", // assisted-mode agent (operates on behalf of a farmer)
  CITIZEN: "CITIZEN",          // self-service farmer / citizen
  EMPLOYEE: "EMPLOYEE",        // generic department employee
  KCC: "KCC",                  // Kisan Call Centre operator
  STADMIN: "STADMIN",          // state admin
  DIRECTOR: "DIRECTOR",
  SECRETARY: "SECRETARY",
  JEEVIKA: "JEEVIKA",
  ANNOUNCEMENT_ADMIN: "ANNOUNCEMENT_ADMIN",
  DATA_REVIEW_ADMIN: "DATA_REVIEW_ADMIN",
});

export const ROLE_GROUPS = Object.freeze({
  /** Logged-in users who share the common landing/profile area (edit-profile, /home). */
  COMMON_AUTHENTICATED: [
    ROLES.AGENT,
    ROLES.EMPLOYEE,
    ROLES.KCC,
    ROLES.STADMIN,
    ROLES.CITIZEN,
  ],

  /**
   * At the shared `/home` URL, these roles see the rewamp landing page
   * (RewampMainLandingPage). Every other COMMON_AUTHENTICATED role falls back to
   * the legacy Homepage dispatcher. Kept separate from AGENT_CITIZEN so landing
   * behaviour can change without touching scheme/grievance access. See
   * HomeLanding in RewampRoutes.js.
   */
  HOME_REWAMP_LANDING: [ROLES.AGENT, ROLES.CITIZEN],

  /** Public before login; after login limited to agent, KCC, citizen (assets, mandi, help). */
  AGENT_KCC_CITIZEN: [ROLES.AGENT, ROLES.KCC, ROLES.CITIZEN],

  /** Public before login; after login limited to agent, citizen (schemes). */
  AGENT_CITIZEN: [ROLES.AGENT, ROLES.CITIZEN],

  /** Assisted-mode agent workflows only (registration, GRM, track, passbook, soil health). */
  AGENT_ONLY: [ROLES.AGENT],

  /** Citizen-only flows (farmer-registration, scheme application-details). */
  CITIZEN_ONLY: [ROLES.CITIZEN],

  /** Carousel / announcement management. */
  ANNOUNCEMENT_ADMIN: [ROLES.ANNOUNCEMENT_ADMIN],

  /** Grievance (PGR/GRM) employee roles — shared with src/components/Utils.js. */
  GRM_EMPLOYEE: pgrRoles,

  /** Decision-support dashboards. */
  DASHBOARD_ADMIN: [ROLES.STADMIN, ROLES.DIRECTOR, ROLES.SECRETARY, ROLES.JEEVIKA],

  /** Data cleanup / review tooling. */
  DATA_REVIEW_ADMIN: [ROLES.DATA_REVIEW_ADMIN],
});

export default ROLE_GROUPS;
